'use client';

import { Container, CustomImage } from '..';
import { PLargeGradient } from '../blood-vision/styleConstants';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useRef } from 'react';
import gsap from 'gsap';
import { CaretUp } from 'assets/icons';
import { ContainerL } from './ContainerL';
import CustomLink from 'components/shared/CustomLink';
import { useLocale, useTranslations } from 'next-intl';
import { splitForStagger } from 'utils/splitWords';
import styles from './AboutPerfectFit.module.css';

export const AboutPerfectFit = () => {
  const t = useTranslations('common');
  const locale = useLocale();
  const intersectionObserver = useRef<IntersectionObserver | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);

  const gsapContext = useGsapContext();

  useIsomorphicLayoutEffect(() => {
    if (!rootRef.current) return;

    intersectionObserver.current = new IntersectionObserver(
      () => {
        gsapContext.add(() => {
          const tl = gsap.timeline({
            defaults: {
              duration: 1,
            },
            scrollTrigger: {
              immediateRender: false,
              trigger: rootRef.current,
              start: 'top 90%',
              end: 'bottom 60%',
              scrub: 0.2,
              invalidateOnRefresh: true,
            },
          });

          tl.set(`.tc-1 > span`, {
            autoAlpha: 0.7,
          });

          tl.fromTo(
            '.tc-1 > span',
            {
              autoAlpha: 0.7,
            },
            {
              autoAlpha: 1,
              duration: 0.35,
              stagger: 0.2,
            },
            0.2,
          );
          tl.addPause(3);
        });
      },
      {
        root: null,
        rootMargin: '20px',
        threshold: 0.01,
      },
    );

    intersectionObserver.current.observe(rootRef.current);

    return () => {
      intersectionObserver.current?.disconnect();
    };
  }, []);

  return (
    <section className={styles.root}>
      <Container className={styles.content} ref={rootRef}>
        {/* literal tc-1 class is the GSAP selector hook — keep it */}
        <PLargeGradient className={`${styles.textContent} tc-1`}>
          {splitForStagger(t('aboutPerfectFit.instructions'), locale).map(
            (part, index) =>
              part.isWord ? (
                <span key={index}>{part.text}</span>
              ) : (
                part.text
              ),
          )}
        </PLargeGradient>
        <div className={styles.disclaimer}>
          <span className={styles.disclaimerTitle}>
            {t('aboutPerfectFit.disclaimerTitle')}
          </span>{' '}
          {t('aboutPerfectFit.disclaimer')}
        </div>
      </Container>

      <ContainerL className={styles.cards}>
        <div className={styles.card}>
          {/* both crops render; CSS shows one per viewport (useWindowSize is 0 on SSR) */}
          <CustomImage
            className={styles.imgDesktop}
            src="web_v2/sizing-kit/sizingKitImgA.png"
            alt=""
            fill
          />
          <CustomImage
            className={styles.imgMobile}
            src="web_v2/sizing-kit/sizingKitA_resp.png"
            alt=""
            fill
          />
          <div className={styles.desc}>
            <p>{t('aboutPerfectFit.sampleRingTip')}</p>
          </div>
        </div>
        <div className={styles.card}>
          <CustomImage
            className={styles.imgDesktop}
            src="web_v2/sizing-kit/sizingKitImgB.png"
            alt=""
            fill
          />
          <CustomImage
            className={styles.imgMobile}
            src="web_v2/sizing-kit/sizingKitB_resp.png"
            alt=""
            fill
          />
          <div className={styles.desc}>
            <span>{t('aboutPerfectFit.wearOvernightTip')}</span>
            <CustomLink
              href="/blog/how-to-use-the-ultrahuman-ring-sizing-kit/"
              className="redirect-to-blog"
            >
              <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {' '}
                {t('aboutPerfectFit.learnMoreBestFit')}
              </span>{' '}
              <CaretUp fill="rgba(255, 255, 255, 0.7)" className={styles.caret} />
            </CustomLink>
          </div>
        </div>
      </ContainerL>
    </section>
  );
};
